import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ChapterMarker, RevealText } from './shared';
import { STOCK } from '../assets';

const MARKETS = [
  { name: 'Japan', note: 'Archive & designer labels, Tokyo' },
  { name: 'USA', note: 'Premium outerwear brands' },
  { name: 'Europe', note: 'Fashion houses & ateliers' },
  { name: 'South Korea', note: 'Contemporary streetwear, Seoul' },
];

const STATS = [
  { value: 40, suffix: '+', label: 'Years of heritage' },
  { value: 4, suffix: '', label: 'Export markets' },
  { value: 14, suffix: '', label: 'Panels per jacket' },
  { value: 47, suffix: '', label: 'Quality checkpoints' },
];

/* ── Animated Counter ─────────────────────────────────────── */
function AnimatedStat({ value, suffix, label, delay }) {
  const ref = useRef(null);
  const numRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isInView || hasAnimated.current || !numRef.current) return;
    hasAnimated.current = true;

    const duration = 2000;
    const start = performance.now();
    const startDelay = delay * 1000;

    const tick = (now) => {
      const elapsed = now - start - startDelay;
      if (elapsed < 0) {
        requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * value);
      if (numRef.current) numRef.current.textContent = current + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isInView, value, suffix, delay]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
    >
      <div ref={numRef} className="font-display text-5xl md:text-6xl font-light text-[#B89B72]">
        0{suffix}
      </div>
      <div className="font-accent text-[9px] text-[#A3A3A3] mt-2">{label}</div>
    </motion.div>
  );
}

export default function Ch9Global() {
  return (
    <section id="ch-9" data-testid="chapter-9" className="relative bg-[#0d0b09] px-6 md:px-16 py-28 md:py-40 overflow-hidden">
      <ChapterMarker num="10" title="Global Manufacturing" />
      <div className="md:grid md:grid-cols-12 md:gap-16">
        <div className="md:col-span-6">
          <RevealText className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
            Crafted in India.<br /><span className="italic text-[#B89B72]">Worn worldwide.</span>
          </RevealText>
          <p className="font-body font-light text-sm text-[#A3A3A3] max-w-md mt-8 leading-relaxed">
            For over three decades, Pakkar Tanveer Exports has produced leather jackets
            for international labels — at scale, sustainably, and without ever
            compromising the hand-finish that defines premium outerwear.
          </p>

          <div className="grid grid-cols-2 gap-x-10 gap-y-10 mt-14">
            {STATS.map((s, i) => (
              <AnimatedStat key={s.label} value={s.value} suffix={s.suffix} label={s.label} delay={i * 0.15} />
            ))}
          </div>
        </div>

        <div className="md:col-span-6 mt-16 md:mt-0">
          <motion.div
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            whileInView={{ clipPath: 'inset(0 0 0% 0)' }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
            className="relative aspect-[16/10] overflow-hidden border border-[#2A2A2A] mb-10"
          >
            <img src={STOCK.factory} alt="PT Exports manufacturing floor" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#0A0A0A]/35" />
            <span className="absolute bottom-4 left-4 font-accent text-[9px] text-[#F5F5F0]/70">THE FLOOR — CHENNAI, INDIA</span>
          </motion.div>

          <div className="space-y-0">
            {MARKETS.map((m, i) => (
              <motion.div
                key={m.name}
                data-testid={`market-${m.name.toLowerCase().replace(' ', '-')}`}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group flex items-baseline justify-between border-b border-[#2A2A2A] py-5 hover:border-[#8C5A35]/60 transition-colors duration-300"
              >
                <span className="font-display text-2xl md:text-3xl font-light group-hover:italic group-hover:text-[#B89B72] transition-colors duration-300">{m.name}</span>
                <span className="font-body text-xs text-[#525252] text-right">{m.note}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
